import { useCallback } from "react";

import type { ChillnnGraphQLError, GraphQLErrors } from "@/lib/apollo-client/custom-error";

import { useChillnnApplicationErrorHandler } from "@/components/features/error-boundary";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

/**
 * GraphQL Errors のハンドリングを行う
 */
export const useGraphQLErrorsHandler = () => {
	const { onThrowChillnnApplicationError, onConfirmIsChillnnApplicationRoleCode } =
		useChillnnApplicationErrorHandler();

	/**
	 * ChillnnGraphQLError から ChillnnApplicationError を取得
	 */
	const findChillnnApplicationError = useCallback(
		(chillnnGraphQLErrors: Array<ChillnnGraphQLError>) =>
			chillnnGraphQLErrors.find((error) => onConfirmIsChillnnApplicationRoleCode(error.extensions.code)),
		[onConfirmIsChillnnApplicationRoleCode],
	);

	/**
	 * @example
	 * ```
	 * Chillnn で定義された GraphQL Errors のハンドリングを行う
	 * 1. Role エラーであれば ErrorBoundary へ通知
	 * 2. ユーザーへ通知を行う必要のあるエラーであればユーザーへ通知
	 * ```
	 */
	const onHandleChillnnGraphQLErrors = useCallback(
		(graphQLErrors: GraphQLErrors) => {
			const chillnnGraphQLErrors = filterChillnnGraphQLErrors(graphQLErrors);
			const hasChillnnGraphQLErrors = confirmHasChillnnGraphQLErrors(chillnnGraphQLErrors);
			if (!hasChillnnGraphQLErrors) return;

			const chillnnApplicationError = findChillnnApplicationError(chillnnGraphQLErrors);
			if (chillnnApplicationError) {
				const { chillnnStatusCode: status, code: statusText } = chillnnApplicationError.extensions;
				/**
				 * アプリケーションエラーとしてハンドリングするため、ErrorBoundary へ通知
				 */
				onThrowChillnnApplicationError({
					status,
					statusText,
				});
			} else {
				/**
				 * ユーザーへの通知
				 */
				for (const error of chillnnGraphQLErrors) {
					const title = error.extensions.chillnnErrorMessageForUser.ja;
					notifyUserEventInfo({
						variant: "error",
						title,
					});
				}
			}
		},
		[findChillnnApplicationError, onThrowChillnnApplicationError],
	);

	/**
	 * Chillnn で定義されていない GraphQL Errors のハンドリングを行う
	 */
	const onHandleNonChillnnGraphQLErrors = useCallback(
		(graphQLErrors: GraphQLErrors, notification: { title: string; description?: string }) => {
			if (graphQLErrors.length === 0) return;

			const chillnnGraphQLErrors = filterChillnnGraphQLErrors(graphQLErrors);
			const hasChillnnGraphQLErrors = confirmHasChillnnGraphQLErrors(chillnnGraphQLErrors);
			if (hasChillnnGraphQLErrors) return;

			/**
			 * ユーザーへ通知
			 */
			notifyUserEventInfo({
				variant: "error",
				title: notification.title,
				description: notification.description ?? "",
			});
		},
		[],
	);

	/**
	 * catch した error が Chillnn で定義されたエラーか判定
	 */
	const onConfirmHasChillnnGraphQLErrors = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(error: any): boolean => {
			/**
			 * 以下の形式でエラーを取得すると決め打ちをしている
			 */
			const graphQLErrors = Array.isArray(error) ? error : (error?.graphQLErrors ?? []);
			const chillnnGraphQLErrors = filterChillnnGraphQLErrors(graphQLErrors as GraphQLErrors);
			return chillnnGraphQLErrors.length > 0;
		},
		[],
	);

	return {
		onHandleChillnnGraphQLErrors,
		onHandleNonChillnnGraphQLErrors,
		onConfirmHasChillnnGraphQLErrors,
	};
};

/**
 * Chillnn エラーの判定
 */
const confirmIsChillnnGraphQLError = (error: GraphQLErrors[number]): error is ChillnnGraphQLError => {
	return (
		error &&
		"extensions" in error &&
		"chillnnErrorMessageForUser" in error.extensions! &&
		"chillnnStatusCode" in error.extensions
	);
};

/**
 * Chillnn エラーを保持しているの判定
 */
const confirmHasChillnnGraphQLErrors = (chillnnGraphQlErrors: Array<ChillnnGraphQLError>): boolean =>
	chillnnGraphQlErrors.length > 0;

/**
 * ChillnnGraphQLError をフィルタリング
 */
const filterChillnnGraphQLErrors = (graphQLErrors: GraphQLErrors): Array<ChillnnGraphQLError> =>
	graphQLErrors.reduce((acc: Array<ChillnnGraphQLError>, error) => {
		const isChillnnGraphQLError = confirmIsChillnnGraphQLError(error);
		if (isChillnnGraphQLError) acc.push(error);

		return acc;
	}, []);
